// Row-level disclosure for the two authored lists inside the Assembly control —
// kit contents and build steps. A kit with twenty items or a recipe with twenty
// steps is a page the customer cannot see the shape of: every row is a full
// fieldset, so the list they are building scrolls away from them as they build
// it. The item list on the quote already answers this — an item folds to a
// one-line recap when the customer adds the next one (QuoteEdit's
// `collapsedLines`) — and this is the same behaviour for a row inside a list,
// held in one place so the two editors cannot drift into two disclosure models.
//
// A row is never unmounted, only `hidden`: it keeps its inputs mounted, its
// uncontrolled state intact, and stays out of the tab order and the
// accessibility tree while folded.
//
// There is deliberately no reveal-on-error primitive here: both lists report
// their failures at LIST level (one banner above the Add button), so no error id
// ever resolves inside a folded subtree. A per-ROW error keyed to a field inside
// the body would need one — the item list expands a collapsed item before
// scrolling to the flagged field (QuoteEdit's revealMiss) — so add the expand
// alongside the error, not after it.
//
// Only a COMPLETE row auto-folds. Folding a half-written one would hide the
// required field the customer has yet to fill, and the list's own validator is
// what decides "complete" (R12) — this module never re-derives it, it takes the
// predicate from the caller.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { ChevronDownIcon, EditIcon } from './ControlIcons.js';

export type CollapsedRows = {
  isCollapsed: (id: string) => boolean;
  toggle: (id: string) => void;
  // Folds away every row the caller reports as finished. Called when a new row
  // is added: adding one says the ones before it are done, and the new row opens
  // against a short list rather than the foot of a long one.
  collapseFinished: (ids: readonly string[]) => void;
  drop: (id: string) => void;
};

// A fold has to outlive the component holding it. An assembly ITEM's editor
// remounts whenever a save replaces its line's synthetic id with the real one it
// mints — the line list is keyed on that id — and that is exactly the moment the
// customer has just folded away the rows they finished. Component state would
// come back empty and the long list they were building would spring open again.
//
// So the folds live outside the component, keyed by the row's own id under the
// QUOTE those rows belong to. The row id alone would not do: a clone copies
// `row_id` and `step_id` through verbatim (only `line_id` is remapped), so a
// store keyed on the id would open the copy already folded the way its source
// was left. Nothing finer than the quote is needed, and nothing finer would be
// SAFE — every candidate for a per-list or per-mount key is either the line id
// the save rewrites or derived from it, so keying on one would lose the folds at
// exactly the remount this store exists to survive. Row ids are minted unique, so
// two lists sharing a quote's set can never read each other's answer; what they
// must not share is the PRUNE, which is why each hook tracks its own ids below.
const collapsed = new Map<string, Set<string>>();
const listeners = new Set<() => void>();
let version = 0;

// The quote whose rows these are. Empty on a mount that never declares one, which
// simply means every such list shares one namespace.
const CollapsedRowsScopeContext = createContext('');

export function CollapsedRowsScope({
  scope,
  children,
}: {
  scope: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <CollapsedRowsScopeContext.Provider value={scope}>
      {children}
    </CollapsedRowsScopeContext.Provider>
  );
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

// Each hook reads the live map during render, so the version is only the signal
// that it moved. Every mounted list is notified, which is what keeps several
// assembly items' kits on one page in step.
function commit(changed: boolean): void {
  if (!changed) return;
  version += 1;
  for (const onChange of listeners) onChange();
}

// A quote whose folds have all been released is dropped rather than left parked
// for the life of the tab.
function release(scope: string, held: Set<string>): void {
  if (held.size === 0) collapsed.delete(scope);
}

// `ids` is every row this list currently holds. A fold on a row that has LEFT the
// list is released, which is what stops a row that goes WITHOUT passing through
// drop() from parking its fold forever — a saved-kit pre-fill replaces the whole
// array (KittingFields' pickBom) rather than removing rows one at a time.
//
// The release walks only the ids this hook has itself rendered. That is what keeps
// two lists sharing a quote's set from pruning each other: the recipe's steps are
// not in the kit list's `ids`, and a key fine enough to separate them would have
// to name the line, which the save rewrites. A remount starts that record empty,
// so the folds it inherits are adopted on the first render and prunable from
// there.
//
// A list that goes away for good — an assembly item removed from the order — is
// the one case nothing releases: its ids stay in the quote's set for the life of
// the tab. There is deliberately no unmount cleanup, because a cleanup cannot
// tell that case apart from the remount this store exists to survive, and would
// throw away the folds at exactly the wrong moment. What is left behind is a
// handful of ids no list will ever name again, which nothing reads.
export function useCollapsedRows(ids: readonly string[]): CollapsedRows {
  useSyncExternalStore(subscribe, () => version);
  const scope = useContext(CollapsedRowsScopeContext);
  const mine = useRef<Set<string>>(new Set());

  const live = ids.join(' ');
  useEffect(() => {
    const liveIds = live.length > 0 ? live.split(' ') : [];
    for (const id of liveIds) mine.current.add(id);
    const held = collapsed.get(scope);
    if (!held) return;
    const keep = new Set(liveIds);
    let changed = false;
    for (const id of mine.current) {
      if (keep.has(id)) continue;
      if (held.delete(id)) changed = true;
      mine.current.delete(id);
    }
    release(scope, held);
    commit(changed);
  }, [live, scope]);

  const isCollapsed = useCallback((id: string) => collapsed.get(scope)?.has(id) === true, [scope]);

  const toggle = useCallback(
    (id: string) => {
      const held = collapsed.get(scope);
      if (!held) {
        collapsed.set(scope, new Set([id]));
      } else if (held.has(id)) {
        held.delete(id);
        release(scope, held);
      } else {
        held.add(id);
      }
      commit(true);
    },
    [scope]
  );

  const collapseFinished = useCallback(
    (rowIds: readonly string[]) => {
      if (rowIds.length === 0) return;
      let held = collapsed.get(scope);
      if (!held) {
        held = new Set<string>();
        collapsed.set(scope, held);
      }
      let changed = false;
      for (const id of rowIds) {
        if (held.has(id)) continue;
        held.add(id);
        changed = true;
      }
      commit(changed);
    },
    [scope]
  );

  const drop = useCallback(
    (id: string) => {
      const held = collapsed.get(scope);
      if (!held) return;
      const changed = held.delete(id);
      mine.current.delete(id);
      release(scope, held);
      commit(changed);
    },
    [scope]
  );

  return { isCollapsed, toggle, collapseFinished, drop };
}

// The chevron, sitting in the row's numbered legend so the control that folds a
// row is beside the number that identifies it. Labelled with the row's own
// number, or a list of N rows presents N identically-named buttons to assistive
// tech.
export function RowDisclosure({
  collapsed,
  bodyId,
  label,
  onToggle,
}: {
  collapsed: boolean;
  bodyId: string;
  label: string;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="disclosure-toggle row-disclosure"
      aria-expanded={!collapsed}
      aria-controls={bodyId}
      aria-label={label}
      onClick={onToggle}
    >
      <ChevronDownIcon className="disclosure-toggle__chevron" />
    </button>
  );
}

// What a folded row says about itself: what it is, then the facts that
// distinguish it from its siblings, then a flag if it is still unfinished — a
// folded row can be the one holding the miss, and the inline red field it refers
// to is hidden.
//
// The recap IS the way back into the row. Customers read a folded row as a
// finished, locked answer and did not find the legend chevron, so the whole line
// is one button with a visible "Edit" cue, and opening it lands focus in the
// row — on the field still asking for an answer if there is one, the first field
// otherwise. No aria-label: the recap's own text, its flag and the cue together
// are the button's name, and the legend around it already numbers the row.
//
// The quote's own folded item uses it too (QuoteEdit's ProjectLineEditor), so
// the two levels of folding open the same way.
const FIELD_SELECTOR = 'input:not([type="hidden"]):not([type="file"]), textarea, select';

export function RowRecap({
  text,
  meta,
  flag,
  bodyId,
  editLabel,
  onExpand,
  className,
}: {
  text: string;
  meta?: ReactNode;
  flag?: string | undefined;
  bodyId: string;
  editLabel: string;
  onExpand: () => void;
  className?: string;
}): JSX.Element {
  function expand(): void {
    onExpand();
    // The body is `hidden` until the fold state commits, so the focus waits a
    // frame for it to be focusable. This button unmounts with the fold, so a
    // body with no field still takes focus itself rather than dropping the
    // customer to <body>.
    requestAnimationFrame(() => {
      const body = document.getElementById(bodyId);
      if (!body) return;
      const target =
        body.querySelector<HTMLElement>(
          ['input', 'textarea', 'select'].map((tag) => `${tag}[aria-invalid="true"]`).join(', ')
        ) ?? body.querySelector<HTMLElement>(FIELD_SELECTOR);
      if (target) {
        target.focus();
      } else {
        body.tabIndex = -1;
        body.focus();
      }
    });
  }
  return (
    <button
      type="button"
      className={className ? `row-recap ${className}` : 'row-recap'}
      aria-expanded={false}
      aria-controls={bodyId}
      onClick={expand}
    >
      <span className="row-recap__text">{text}</span>
      {meta !== undefined && meta !== null && <span className="row-recap__meta">{meta}</span>}
      {flag && <span className="row-recap__flag">{flag}</span>}
      <span className="row-recap__edit">
        <EditIcon />
        {editLabel}
      </span>
    </button>
  );
}
